import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { MissedReports } from "@/components/MissedReports";
import { Plus, Zap, Filter as FilterIcon, Play, Trash2, Edit3, Power, X, ChevronRight, Workflow, History, CheckCircle2, AlertCircle, FlaskConical, AlertTriangle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND}/api`;

const STEP_COLORS = {
  trigger: { bg: "bg-orange-500/15", border: "border-orange-500/40", text: "text-orange-300", icon: Zap },
  filter:  { bg: "bg-sky-500/15", border: "border-sky-500/40", text: "text-sky-300", icon: FilterIcon },
  action:  { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-300", icon: Play },
};

function StepBadge({ kind, label }) {
  const s = STEP_COLORS[kind];
  const Icon = s.icon;
  return (
    <div className={`flex items-center gap-2 rounded-lg border ${s.border} ${s.bg} px-3 py-2`}>
      <Icon className={`h-4 w-4 ${s.text}`} />
      <div>
        <div className="text-[9px] uppercase tracking-wider text-zinc-500">{kind === "trigger" ? "Declanșator" : kind === "filter" ? "Filtru" : "Acțiune"}</div>
        <div className={`text-sm font-medium ${s.text}`}>{label}</div>
      </div>
    </div>
  );
}

function StepArrow() { return <ChevronRight className="h-5 w-5 text-zinc-700 mx-1 shrink-0" />; }

// ---------- Builder Modal ----------
function BuilderModal({ open, automation, catalog, onClose, onSave }) {
  const [form, setForm] = useState({
    name: "", is_active: false,
    trigger: { type: "", config: {} },
    filter: { conditions: [], logic: "and" },
    action: { type: "", config: {} },
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (automation) setForm(JSON.parse(JSON.stringify(automation)));
    else setForm({ name: "", is_active: false, trigger: { type: "", config: {} }, filter: { conditions: [], logic: "and" }, action: { type: "", config: {} } });
  }, [automation, open]);

  if (!open) return null;
  const triggerDef = catalog?.triggers?.find((t) => t.key === form.trigger.type);
  const actionDef  = catalog?.actions?.find((a) => a.key === form.action.type);

  const addCondition = () => setForm({ ...form, filter: { ...form.filter, conditions: [...(form.filter.conditions || []), { field: "", op: "equals", value: "" }] }});
  const updateCondition = (i, patch) => {
    const conds = [...(form.filter.conditions || [])];
    conds[i] = { ...conds[i], ...patch };
    setForm({ ...form, filter: { ...form.filter, conditions: conds }});
  };
  const removeCondition = (i) => setForm({ ...form, filter: { ...form.filter, conditions: (form.filter.conditions || []).filter((_, idx) => idx !== i) }});
  const setLogic = (logic) => setForm({ ...form, filter: { ...form.filter, logic }});

  const submit = async () => {
    if (!form.name || !form.trigger.type || !form.action.type) { setErr("Nume, declanșator și acțiune sunt obligatorii"); return; }
    // Validate required action config fields (textarea/text without value when key looks required)
    if (form.action.type === "webhook" && !(form.action.config?.url || "").trim()) {
      setErr("Acțiunea Webhook necesită un URL.");
      return;
    }
    if (form.action.type === "add_note" && !(form.action.config?.note || "").trim()) {
      setErr("Acțiunea „Adaugă notă\" necesită conținutul notei.");
      return;
    }
    setSaving(true); setErr("");
    try {
      await onSave(form);
      onClose();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-zinc-800 bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-xl font-bold text-zinc-100">{automation ? "Editează automatizare" : "Automatizare nouă"}</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X className="h-5 w-5" /></button>
        </div>

        {err && <div className="mb-4 rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-300">{err}</div>}

        <div className="mb-5">
          <label className="mb-1.5 block text-[10px] uppercase tracking-wider text-zinc-500">Nume automatizare</label>
          <input data-testid="automation-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="ex: Factură automată după AWB BAU"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
        </div>

        {/* TRIGGER */}
        <div className="mb-5 rounded-xl border border-orange-500/30 bg-orange-500/5 p-4">
          <div className="mb-2 flex items-center gap-2"><Zap className="h-4 w-4 text-orange-400" /><span className="text-sm font-semibold text-orange-300">1. Declanșator</span></div>
          <select data-testid="automation-trigger" value={form.trigger.type} onChange={(e) => setForm({ ...form, trigger: { type: e.target.value, config: {} }})}
            className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100">
            <option value="">— alege un declanșator —</option>
            {catalog?.triggers?.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          {triggerDef?.config_fields?.map((cf) => (
            <div key={cf.key} className="mt-3">
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">{cf.label}</label>
              {cf.type === "select" ? (
                <select value={form.trigger.config[cf.key] ?? ""} onChange={(e) => setForm({ ...form, trigger: { ...form.trigger, config: { ...form.trigger.config, [cf.key]: e.target.value }}})}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100">
                  <option value="">—</option>
                  {cf.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <input value={form.trigger.config[cf.key] ?? ""} onChange={(e) => setForm({ ...form, trigger: { ...form.trigger, config: { ...form.trigger.config, [cf.key]: e.target.value }}})}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
              )}
            </div>
          ))}
        </div>

        {/* FILTER */}
        <div className="mb-5 rounded-xl border border-sky-500/30 bg-sky-500/5 p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2"><FilterIcon className="h-4 w-4 text-sky-400" /><span className="text-sm font-semibold text-sky-300">2. Filtru (opțional)</span></div>
            <button onClick={addCondition} className="rounded-md border border-sky-500/40 px-2 py-1 text-xs text-sky-200 hover:bg-sky-500/10">+ Condiție</button>
          </div>
          {(form.filter.conditions || []).length === 0 && <p className="text-xs text-zinc-500">Acțiunea se execută pentru toate evenimentele care declanșează. Adaugă condiții pentru a filtra.</p>}
          {/* Impossible-config warning: AND mode with multiple `equals` on the
              same field having different values can never match any single order. */}
          {(() => {
            const conds = form.filter.conditions || [];
            const logic = form.filter.logic || "and";
            if (logic !== "and" || conds.length < 2) return null;
            const byField = {};
            for (const c of conds) {
              if (c.op !== "equals" || !c.field || !c.value) continue;
              byField[c.field] = byField[c.field] || new Set();
              byField[c.field].add(String(c.value));
            }
            const offending = Object.entries(byField).filter(([, vals]) => vals.size >= 2);
            if (offending.length === 0) return null;
            const labelByKey = Object.fromEntries((catalog?.filters || []).map((f) => [f.key, f.label]));
            return (
              <div data-testid="filter-impossible-warning" className="mb-3 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-300" />
                <div className="flex-1">
                  <p className="font-semibold">Configurație imposibilă cu „ȘI"</p>
                  <p className="mt-1 text-amber-100/80">
                    O comandă nu poate să fie simultan cu {offending.map(([f, vals]) => (
                      <span key={f}><b>{labelByKey[f] || f}</b> egal cu {Array.from(vals).map((v, i) => <span key={v}>{i > 0 ? " și " : ""}<i>{v}</i></span>)}</span>
                    ))}. Cu „ȘI", filtrul nu va match-ui niciodată nicio comandă, deci automatizarea nu se va declanșa.
                  </p>
                  <button
                    data-testid="filter-fix-to-or"
                    onClick={() => setLogic("or")}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-400/60 bg-amber-500/20 px-2.5 py-1 text-[11px] font-semibold text-amber-50 hover:bg-amber-500/30"
                  >
                    Schimbă în „SAU (orice condiție)"
                  </button>
                </div>
              </div>
            );
          })()}
          {(form.filter.conditions || []).length > 1 && (
            <div className="mb-3 inline-flex items-center gap-1 rounded-lg border border-sky-500/30 bg-zinc-950 p-0.5 text-[11px]" data-testid="filter-logic-toggle">
              <button onClick={() => setLogic("and")} data-testid="filter-logic-and"
                className={`rounded-md px-3 py-1 font-medium transition-colors ${(form.filter.logic || "and") === "and" ? "bg-sky-500/30 text-sky-100" : "text-zinc-500 hover:text-zinc-300"}`}>
                ȘI (toate condițiile)
              </button>
              <button onClick={() => setLogic("or")} data-testid="filter-logic-or"
                className={`rounded-md px-3 py-1 font-medium transition-colors ${form.filter.logic === "or" ? "bg-sky-500/30 text-sky-100" : "text-zinc-500 hover:text-zinc-300"}`}>
                SAU (orice condiție)
              </button>
            </div>
          )}
          {(form.filter.conditions || []).map((cond, i) => {
            const fd = catalog?.filters?.find((f) => f.key === cond.field);
            return (
              <div key={i} className="mt-2 flex flex-wrap items-center gap-2">
                {i > 0 && (
                  <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-sky-200">
                    {(form.filter.logic || "and") === "or" ? "sau" : "și"}
                  </span>
                )}
                <select value={cond.field} onChange={(e) => updateCondition(i, { field: e.target.value, value: "" })}
                  className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-100">
                  <option value="">câmp</option>
                  {catalog?.filters?.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
                <select value={cond.op} onChange={(e) => updateCondition(i, { op: e.target.value })}
                  className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-100">
                  {(fd?.ops || ["equals"]).map((op) => <option key={op} value={op}>{op}</option>)}
                </select>
                {fd?.value_options ? (
                  <select value={cond.value} onChange={(e) => updateCondition(i, { value: e.target.value })}
                    className="flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-100">
                    <option value="">valoare</option>
                    {fd.value_options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input value={cond.value} onChange={(e) => updateCondition(i, { value: e.target.value })}
                    placeholder="valoare" className="flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-100" />
                )}
                <button onClick={() => removeCondition(i)} className="text-zinc-500 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            );
          })}
        </div>

        {/* ACTION */}
        <div className="mb-5 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div className="mb-2 flex items-center gap-2"><Play className="h-4 w-4 text-emerald-400" /><span className="text-sm font-semibold text-emerald-300">3. Acțiune</span></div>
          <select data-testid="automation-action" value={form.action.type} onChange={(e) => setForm({ ...form, action: { type: e.target.value, config: {} }})}
            className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100">
            <option value="">— alege o acțiune —</option>
            {catalog?.actions?.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
          {actionDef?.config_fields?.map((cf) => (
            <div key={cf.key} className="mt-3">
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">{cf.label}</label>
              {cf.type === "select" ? (
                <select data-testid={`automation-action-${cf.key}`} value={form.action.config[cf.key] ?? ""} onChange={(e) => setForm({ ...form, action: { ...form.action, config: { ...form.action.config, [cf.key]: e.target.value }}})}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100">
                  <option value="">—</option>
                  {cf.options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
                </select>
              ) : cf.type === "boolean" ? (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input data-testid={`automation-action-${cf.key}`} type="checkbox" checked={Boolean(form.action.config[cf.key] ?? cf.default)} onChange={(e) => setForm({ ...form, action: { ...form.action, config: { ...form.action.config, [cf.key]: e.target.checked }}})}
                    className="accent-emerald-500" />
                  <span className="text-sm text-zinc-200">{form.action.config[cf.key] ?? cf.default ? "Activat" : "Dezactivat"}</span>
                </label>
              ) : cf.type === "template_select" ? (
                <select data-testid={`automation-action-${cf.key}`} value={form.action.config[cf.key] ?? ""} onChange={(e) => setForm({ ...form, action: { ...form.action, config: { ...form.action.config, [cf.key]: e.target.value }}})}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100">
                  <option value="">— niciun șablon (folosește subiect/conținut de mai jos) —</option>
                  {(catalog?.email_templates || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              ) : cf.type === "wa_template_select" ? (
                <select data-testid={`automation-action-${cf.key}`} value={form.action.config[cf.key] ?? ""} onChange={(e) => setForm({ ...form, action: { ...form.action, config: { ...form.action.config, [cf.key]: e.target.value }}})}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100">
                  <option value="">— alege un șablon aprobat —</option>
                  {((catalog?.whatsapp_templates || []).filter((t) => t.meta_status === "APPROVED")).map((t) => (
                    <option key={t.id} value={t.id}>{t.name} ({t.category})</option>
                  ))}
                  {(catalog?.whatsapp_templates || []).filter((t) => t.meta_status === "APPROVED").length === 0 && (
                    <option value="" disabled>Niciun șablon APPROVED. Creează unul în Șabloane → WhatsApp și submit-ează la Meta.</option>
                  )}
                </select>
              ) : cf.type === "textarea" ? (
                <textarea data-testid={`automation-action-${cf.key}`} value={form.action.config[cf.key] ?? ""} onChange={(e) => setForm({ ...form, action: { ...form.action, config: { ...form.action.config, [cf.key]: e.target.value }}})}
                  placeholder={cf.label}
                  rows={3} className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
              ) : cf.type === "number" ? (
                <input type="number" min="0" step="1" data-testid={`automation-action-${cf.key}`}
                  value={form.action.config[cf.key] ?? (cf.default ?? 0)}
                  onChange={(e) => setForm({ ...form, action: { ...form.action, config: { ...form.action.config, [cf.key]: Number(e.target.value) }}})}
                  placeholder="0"
                  className="w-40 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
              ) : (
                <input data-testid={`automation-action-${cf.key}`} value={form.action.config[cf.key] ?? ""} onChange={(e) => setForm({ ...form, action: { ...form.action, config: { ...form.action.config, [cf.key]: e.target.value }}})}
                  placeholder={cf.label}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-2">
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-orange-500" />
            Activează automatizarea
          </label>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-md border border-zinc-800 px-4 py-2 text-sm text-zinc-300 hover:border-zinc-700">Renunță</button>
            <button data-testid="automation-save" onClick={submit} disabled={saving}
              className="rounded-md bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-400 disabled:bg-zinc-700">
              {saving ? "Se salvează..." : "Salvează"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Run History Modal ----------
function RunsModal({ open, automation, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !automation) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/automations/${automation.id}/runs`, { params: { limit: 50 } })
      .then(r => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, automation]);
  if (!open || !automation) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div data-testid="automation-runs-modal" className="w-full max-w-2xl rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Istoric rulări</div>
            <h3 className="text-sm font-semibold text-zinc-100">{automation.name}</h3>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200" data-testid="runs-modal-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && <div className="px-5 py-6 text-center text-xs text-zinc-500">Se încarcă...</div>}
          {!loading && items.length === 0 && (
            <div className="px-5 py-10 text-center text-xs text-zinc-500">
              <History className="mx-auto mb-2 h-6 w-6 text-zinc-700" />
              Niciun istoric. Activează automatizarea și așteaptă evenimentul declanșator.
            </div>
          )}
          {items.map((r) => (
            <div key={r.id} className="border-b border-zinc-900 px-5 py-3">
              <div className="flex items-start gap-2">
                {r.ok
                  ? <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" />
                  : <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-400" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-zinc-200">{r.event}</span>
                    <span className="text-[10px] text-zinc-600">{r.ran_at ? new Date(r.ran_at).toLocaleString("ro-RO") : ""}</span>
                  </div>
                  {r.ok && r.result && (
                    <pre className="mt-1 max-h-24 overflow-auto rounded border border-zinc-900 bg-zinc-900/60 px-2 py-1 text-[10px] text-zinc-400">{typeof r.result === "string" ? r.result : JSON.stringify(r.result, null, 2)}</pre>
                  )}
                  {!r.ok && r.error && (
                    <div className="mt-1 rounded border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[10px] text-rose-200">{r.error}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Main Page ----------
export default function Automations() {
  const [list, setList] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [runsFor, setRunsFor] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        axios.get(`${API}/automations`),
        axios.get(`${API}/automations/catalog`),
        axios.get(`${API}/email-templates`).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/whatsapp-templates`).catch(() => ({ data: { items: [] } })),
      ]);
      setList(r1.data?.items || []);
      setCatalog({
        ...(r2.data || {}),
        email_templates: r3.data?.items || [],
        whatsapp_templates: r4.data?.items || [],
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSave = async (form) => {
    if (form.id) await axios.put(`${API}/automations/${form.id}`, form);
    else await axios.post(`${API}/automations`, form);
    await refresh();
  };
  const handleToggle = async (a) => { await axios.post(`${API}/automations/${a.id}/toggle`); await refresh(); };
  const handleDelete = async (a) => { if (!window.confirm(`Șterg „${a.name}"?`)) return; await axios.delete(`${API}/automations/${a.id}`); await refresh(); };
  const handleTestRun = async (a) => {
    if (!window.confirm(`Rulează „${a.name}" împotriva ultimei comenzi din cache? Acțiunea va fi executată REAL (e.g. va trimite email, va genera AWB).`)) return;
    try {
      const r = await axios.post(`${API}/automations/${a.id}/test-run`, {});
      alert(`Test rulat cu succes pe order ${r.data?.order_id}. Vezi istoricul pentru detalii.`);
      await refresh();
    } catch (e) {
      alert(`Test eșuat: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleReplayAwbTransitions = async () => {
    const to_state = window.prompt(
      "Rejucă automatizările pentru comenzile care au deja acest awb_state:",
      "return_delivered"
    );
    if (!to_state) return;
    const days = parseInt(window.prompt("Câte zile în urmă să caut? (max 90)", "30") || "0", 10);
    if (!days) return;
    try {
      // 1) dry-run to preview
      const dry = await axios.post(`${API}/admin/replay-awb-state-automations`, null, {
        params: { to_state, days, dry_run: true },
      });
      const cnt = dry.data?.count || 0;
      if (cnt === 0) { alert("Nu am găsit nicio comandă care să necesite rejucare."); return; }
      const sample = (dry.data?.candidates || []).slice(0, 5).map((c) =>
        `#${c.gomag_number || c.emag_id} · ${c.awb} · ${c.integration}`).join("\n");
      if (!window.confirm(
        `Voi rejuca automatizările pentru ${cnt} comenzi în starea „${to_state}"\n\n` +
        `Exemple:\n${sample}${cnt > 5 ? `\n… și încă ${cnt - 5}` : ""}\n\n` +
        `Confirmi? Se vor executa ACȚIUNILE reale (ex: se va emite storno, se va trimite email).`
      )) return;
      const r = await axios.post(`${API}/admin/replay-awb-state-automations`, null, {
        params: { to_state, days, dry_run: false },
      });
      alert(`Rejucate: ${r.data?.fired || 0} din ${r.data?.count || 0}. Erori: ${(r.data?.errors || []).length}. Vezi „Istoric rulări".`);
      await refresh();
    } catch (e) {
      alert(`Eșuat: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const labelFor = (kind, type) => {
    const arr = catalog?.[kind === "trigger" ? "triggers" : "actions"];
    return arr?.find((x) => x.key === type)?.label || type || "—";
  };

  return (
    <div className="space-y-5 p-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] text-zinc-500">Panou de control / <span className="text-zinc-300">Automatizări</span></div>
          <h1 className="mt-1 text-2xl font-bold text-zinc-50">Automatizări</h1>
          <p className="mt-1 text-sm text-zinc-500">Construiește fluxuri: declanșator → filtru → acțiune.</p>
        </div>
        <button data-testid="automation-new" onClick={() => { setEditing(null); setOpenModal(true); }}
          className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-400">
          <Plus className="h-4 w-4" /> Flux nou
        </button>
      </div>

      <MissedReports />

      <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3">
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/20">
          <History className="h-3.5 w-3.5 text-amber-300" />
        </div>
        <div className="flex-1 text-xs text-amber-100">
          <p className="font-semibold">Rejucă tranziții AWB pierdute</p>
          <p className="mt-0.5 text-amber-200/70">
            Pentru comenzile ale căror AWB deja este în starea dorită (ex: <i>return_delivered</i>) dar automatizarea nu s-a executat — cheamă din nou trigger-ul acum.
          </p>
        </div>
        <button
          data-testid="replay-awb-transitions-btn"
          onClick={handleReplayAwbTransitions}
          className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-100 hover:bg-amber-500/20"
        >
          Rulează
        </button>
      </div>

      <div data-testid="engine-banner" className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3">
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20">
          <Zap className="h-3.5 w-3.5 text-emerald-300" />
        </div>
        <div className="text-xs text-emerald-100">
          <p className="font-semibold">Motorul de execuție este activ.</p>
          <p className="mt-0.5 text-emerald-200/70">
            Doar fluxurile cu butonul <Power className="inline h-3 w-3" /> ACTIV vor rula. Declanșatori: comandă nouă, schimbare status, AWB/factură generate, programat (oră/zi/săpt./lună).
          </p>
        </div>
      </div>

      {loading && <div className="text-sm text-zinc-500">Se încarcă...</div>}

      {!loading && list.length === 0 && (
        <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-950/50 p-12 text-center">
          <Workflow className="mx-auto h-10 w-10 text-zinc-700" />
          <p className="mt-3 text-sm text-zinc-400">Nu ai încă automatizări.</p>
          <p className="text-xs text-zinc-600">Apasă „Flux nou" pentru a construi prima.</p>
        </div>
      )}

      <div className="space-y-3">
        {list.map((a) => (
          <div key={a.id} data-testid={`automation-row-${a.id}`} className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => handleToggle(a)} title={a.is_active ? "Dezactivează" : "Activează"}
                  className={`flex h-8 w-8 items-center justify-center rounded-full border ${a.is_active ? "border-emerald-500/50 bg-emerald-500/20 text-emerald-300" : "border-zinc-700 bg-zinc-900 text-zinc-500"}`}>
                  <Power className="h-3.5 w-3.5" />
                </button>
                <div>
                  <div className="text-sm font-semibold text-zinc-100">{a.name}</div>
                  <div className="text-[10px] text-zinc-500">{a.is_active ? "Activ" : "Inactiv"} · Rulări: {a.run_count || 0}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => handleTestRun(a)} className="rounded-md p-1.5 text-zinc-500 hover:bg-orange-500/10 hover:text-orange-300" title="Test rulează acum" data-testid={`automation-test-${a.id}`}><FlaskConical className="h-4 w-4" /></button>
                <button onClick={() => setRunsFor(a)} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-sky-300" title="Istoric rulări" data-testid={`automation-runs-${a.id}`}><History className="h-4 w-4" /></button>
                <button onClick={() => { setEditing(a); setOpenModal(true); }} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200" title="Editează"><Edit3 className="h-4 w-4" /></button>
                <button onClick={() => handleDelete(a)} className="rounded-md p-1.5 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400" title="Șterge"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StepBadge kind="trigger" label={labelFor("trigger", a.trigger?.type)} />
              <StepArrow />
              <StepBadge kind="filter" label={`${a.filter?.conditions?.length || 0} ${(a.filter?.conditions?.length || 0) === 1 ? "condiție" : "condiții"}`} />
              <StepArrow />
              <StepBadge kind="action" label={labelFor("action", a.action?.type)} />
            </div>
          </div>
        ))}
      </div>

      <BuilderModal open={openModal} automation={editing} catalog={catalog}
        onClose={() => { setOpenModal(false); setEditing(null); }} onSave={handleSave} />
      <RunsModal open={!!runsFor} automation={runsFor} onClose={() => setRunsFor(null)} />
    </div>
  );
}
